import { useState } from "react";
import {
  Link as LinkIcon,
  Bell,
  MoreHorizontal,
  Printer,
  FileText,
  Archive,
  Trash2,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { ConfirmDialog } from "@/components/v2/confirm-dialog";

/**
 * Per-page quick-actions cluster rendered on every v2 detail page (ADV-810).
 *
 * Locked-in defaults (see thoughts/shared/plans/2026-05-28-detail-page-actions-ADV-810.md):
 * - Copy link → writes window.location.origin + pathname; sonner success toast.
 * - Subscribe (bell) → disabled placeholder; no backend yet.
 * - Print → window.print(); no print stylesheet work here.
 * - Export PDF → disabled menu item; no PDF service yet.
 * - Archive/Delete → optional; menu items omitted when callbacks not provided.
 */
interface DetailPageActionsProps {
  entityLabel: string;
  onArchive?: () => void | Promise<void>;
  onDelete?: () => void | Promise<void>;
}

export function DetailPageActions({
  entityLabel,
  onArchive,
  onDelete,
}: DetailPageActionsProps) {
  const [confirm, setConfirm] = useState<null | "archive" | "delete">(null);

  async function copyLink() {
    try {
      await navigator.clipboard.writeText(
        window.location.origin + window.location.pathname,
      );
      toast.success("Link copied");
    } catch {
      toast.error("Couldn't copy link");
    }
  }

  return (
    <>
      <Button
        variant="ghost"
        size="sm"
        className="h-8 w-8 p-0"
        aria-label="Copy link"
        onClick={copyLink}
      >
        <LinkIcon className="h-4 w-4" />
      </Button>
      <Button
        variant="ghost"
        size="sm"
        className="h-8 w-8 p-0"
        aria-label="Subscribe to updates"
        title="Notifications coming soon"
        disabled
      >
        <Bell className="h-4 w-4" />
      </Button>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className="h-8 w-8 p-0"
            aria-label="More actions"
          >
            <MoreHorizontal className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-48">
          <DropdownMenuItem onClick={() => window.print()}>
            <Printer className="mr-2 h-3.5 w-3.5" />
            Print
          </DropdownMenuItem>
          <DropdownMenuItem disabled>
            <FileText className="mr-2 h-3.5 w-3.5" />
            Export PDF
            <span className="ml-auto text-xs text-[color:var(--color-text-lo)]">
              Coming soon
            </span>
          </DropdownMenuItem>
          {(onArchive || onDelete) && <DropdownMenuSeparator />}
          {onArchive && (
            <DropdownMenuItem
              className="text-destructive focus:text-destructive"
              onClick={() => setConfirm("archive")}
            >
              <Archive className="mr-2 h-3.5 w-3.5" />
              Archive
            </DropdownMenuItem>
          )}
          {onDelete && (
            <DropdownMenuItem
              className="text-destructive focus:text-destructive"
              onClick={() => setConfirm("delete")}
            >
              <Trash2 className="mr-2 h-3.5 w-3.5" />
              Delete
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      <ConfirmDialog
        open={confirm === "archive"}
        onOpenChange={(o) => !o && setConfirm(null)}
        title={`Archive ${entityLabel}?`}
        description={`This ${entityLabel.toLowerCase()} will be hidden from active lists. You can restore it later.`}
        confirmLabel="Archive"
        variant="destructive"
        onConfirm={async () => {
          await onArchive?.();
          setConfirm(null);
        }}
      />
      <ConfirmDialog
        open={confirm === "delete"}
        onOpenChange={(o) => !o && setConfirm(null)}
        title={`Delete ${entityLabel}?`}
        description={`This will permanently delete this ${entityLabel.toLowerCase()}. This cannot be undone.`}
        confirmLabel="Delete"
        variant="destructive"
        onConfirm={async () => {
          await onDelete?.();
          setConfirm(null);
        }}
      />
    </>
  );
}
